/**
 * CookieBanner — Bandeau de consentement aux cookies, conforme CNIL.
 *
 * Recommandations CNIL (lignes directrices Cookies 17 sept 2020) :
 *  - Refus aussi simple que l'acceptation (1 clic)
 *  - Choix granulaire possible (techniques / mesure d'audience / marketing)
 *  - Pas de cookies non essentiels avant consentement
 *  - Conservation du consentement 6 mois max (CNIL recommande 13 mois)
 *  - Possibilité de retirer le consentement à tout moment
 *
 * VisionPos utilise quasi exclusivement des cookies techniques (auth Supabase,
 * session) qui sont exemptés de consentement (art. 82 LIL). Les seuls cookies
 * non essentiels sont éventuellement Sentry (mesure perf) → strictement
 * anonymisé. Pas de tracking marketing tiers.
 */
import { useEffect, useState } from 'react';
import { Cookie, X, ShieldCheck, Settings2 } from 'lucide-react';

const COOKIE_KEY = 'visionpos_cookie_consent';
const COOKIE_VERSION = 'v1';

type CookieChoice = {
  version: string;
  timestamp: number;
  essential: true; // toujours requis pour fonctionnement
  performance: boolean; // Sentry, perf monitoring
  marketing: boolean; // ad tracking — pas utilisé actuellement, mais préparé
};

function readConsent(): CookieChoice | null {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== COOKIE_VERSION) return null;
    // Expiration 6 mois (recommandation CNIL)
    const sixMonths = 6 * 30 * 24 * 3600 * 1000;
    if (Date.now() - parsed.timestamp > sixMonths) return null;
    return parsed as CookieChoice;
  } catch {
    return null;
  }
}

function writeConsent(choice: Omit<CookieChoice, 'version' | 'timestamp' | 'essential'>) {
  const payload: CookieChoice = {
    version: COOKIE_VERSION,
    timestamp: Date.now(),
    essential: true,
    ...choice,
  };
  localStorage.setItem(COOKIE_KEY, JSON.stringify(payload));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [performance, setPerformance] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    // Affiche le bandeau si pas de consentement valide
    const consent = readConsent();
    if (!consent) setVisible(true);
  }, []);

  const acceptAll = () => {
    writeConsent({ performance: true, marketing: true });
    setVisible(false);
  };

  const refuseAll = () => {
    writeConsent({ performance: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    writeConsent({ performance, marketing });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Consentement aux cookies"
      aria-modal="true"
      className="fixed bottom-0 left-0 right-0 z-[1000] border-t bg-background shadow-2xl"
    >
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        {!showSettings ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Cookie className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <div className="flex-1 text-sm">
                <p className="mb-1 font-semibold">Vos préférences cookies</p>
                <p className="text-muted-foreground">
                  Nous utilisons des cookies <strong>strictement nécessaires</strong> au
                  fonctionnement (authentification, session) qui sont exemptés de consentement. Avec
                  votre accord, nous utilisons aussi des cookies de{' '}
                  <strong>mesure de performance</strong> (Sentry, anonymisé) pour améliorer le
                  service.
                  <strong> Aucun tracking publicitaire tiers.</strong>
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-end gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                <Settings2 className="h-4 w-4" />
                Personnaliser
              </button>
              <button
                type="button"
                onClick={refuseAll}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                Tout refuser
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
              >
                <ShieldCheck className="h-4 w-4" />
                Tout accepter
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold">Préférences détaillées</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Le consentement est conservé 6 mois. Vous pouvez le modifier à tout moment.
                </p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                aria-label="Fermer"
                className="rounded p-1 hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <label className="flex cursor-not-allowed items-start gap-3 rounded-md border bg-muted/30 p-3">
                <input type="checkbox" checked disabled className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Strictement nécessaires</p>
                    <span className="text-xs text-muted-foreground">Toujours actifs</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Authentification, sessions, panier de vente. Indispensables au fonctionnement,
                    exemptés de consentement (art. 82 LIL).
                  </p>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/30">
                <input
                  type="checkbox"
                  checked={performance}
                  onChange={(e) => setPerformance(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Mesure de performance</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Sentry — détection d'erreurs et performance applicative. Données anonymisées,
                    hébergement UE, conservation 90 jours.
                  </p>
                </div>
              </label>

              <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/30">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">Marketing & publicité</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Aucun cookie marketing utilisé actuellement. Ce toggle reste disponible si nous
                    activons à l'avenir des intégrations type Google Ads ou retargeting.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex flex-col justify-end gap-2 border-t pt-2 sm:flex-row">
              <button
                type="button"
                onClick={refuseAll}
                className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
              >
                Tout refuser
              </button>
              <button
                type="button"
                onClick={saveCustom}
                className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:opacity-90"
              >
                Enregistrer mes choix
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Helper public pour réinitialiser le consentement (footer "Mes préférences cookies") */
export function resetCookieConsent() {
  localStorage.removeItem(COOKIE_KEY);
  window.location.reload();
}

/** Helper pour lire le consentement courant (utile pour conditionner Sentry, etc.) */
export function getCookieConsent(): CookieChoice | null {
  return readConsent();
}
